import {
  Search as SearchIcon,
  StarRate as StarRateIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import {
  CounterpartyCreateUpdate,
  CounterpartyDropDown,
  CounterpartyType,
  Invoice,
  InvoicePatch,
  InvoiceSection
} from 'openapi';

import { useCompanies } from 'context/CompanyContext';
import { useTranslations } from 'context/TranslationContext';

import { useDebounce } from 'hooks/useDebounce';

import {
  COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
  INITIAL_IBAN_ITEM
} from 'utils/constants/constants';
import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';
import { uniqueIbanValidation } from 'utils/helpers/ibanHelper';
import {
  getCounterPartySearchOptionLabel,
  getSupplierDataFormValues,
  getSupplierDefaultValues,
  validateIban,
  validateRegistrationNumber,
  validateVatNumber
} from 'utils/helpers/invoiceHelpers';
import { InvoiceSectionKey } from 'utils/interfaces/InvoiceProps';

import { arrayFieldFormContainer } from 'styles/components/Common';
import {
  smallVerificationFieldWidth,
  verificationFormFields,
  verificationFormFieldsWrapper,
  verificationFormItem
} from 'styles/pages/InvoiceVerificationStyle';

import { ActionButtons } from './ActionButtons';
import { AdditionalInvoiceFields } from './ReceiverForm';

interface SupplierFormProps {
  currentInvoice: Invoice;
  handleVerify: (
    values: InvoicePatch & AdditionalInvoiceFields,
    section: InvoiceSectionKey
  ) => void;
  handleBack: (section: InvoiceSection) => void;
  handleGoToMiddleStep: () => void;
  saveInvoiceSection: (
    values: InvoicePatch,
    section: InvoiceSectionKey
  ) => Promise<void>;
  setCounterpartyFormData: React.Dispatch<
    React.SetStateAction<Invoice | undefined>
  >;
  setCpIsNotFoundInDatabase: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SupplierForm = ({
  currentInvoice,
  handleVerify,
  handleBack,
  handleGoToMiddleStep,
  saveInvoiceSection,
  setCounterpartyFormData,
  setCpIsNotFoundInDatabase
}: SupplierFormProps) => {
  const { translate } = useTranslations();
  const { checkIfCounterpartyExists, createNewCounterParty } =
    useCounterPartyController();
  const { getAllDropDownCounterpartiesByCompany } =
    useAdditionalFieldsController();
  const companyName = useCompanies().companiesList.find(
    (company) => company.id === currentInvoice.companyId
  )?.name;

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<CounterpartyDropDown[]>([]);
  const [searchParams, setSearchParams] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCounterPartyId, setSelectedCounterPartyId] = useState<
    number | null
  >(null);
  const [isExisting, setIsExisting] = useState<boolean>(true);
  const [isFoundInDatabase, setIsFoundInDatabase] = useState<boolean>(true);

  const notEnoughCharactersToSearch = useMemo(() => {
    return (searchParams || '').length < COUNTERPARTY_SEARCH_MIN_CHARACTERS;
  }, [searchParams]);

  const {
    control,
    formState,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    watch,
    trigger,
    reset
  } = useForm<Invoice>({
    mode: 'onChange',
    defaultValues: getSupplierDefaultValues(currentInvoice)
  });

  const { append } = useFieldArray({
    control,
    name: 'ibans',
    keyName: 'fieldId'
  });

  const [isFormValid, setIsFormValid] = useState<boolean>(true);

  const watchedCounterpartyRegistrationNumber = watch(
    'counterpartyRegistrationNumber'
  );
  const watchedCounterpartyVatNumber = watch('counterpartyVatNumber');
  const watchedIsReimbursement = watch('isReimbursement');
  const watchedIbanList = watch('ibans');

  const getCounterParties = useCallback(async () => {
    if (notEnoughCharactersToSearch) {
      setIsLoading(false);
      setOptions([]);
      return;
    }

    const counterpartyType = watchedIsReimbursement
      ? CounterpartyType.REIMBURSEMENT
      : CounterpartyType.SUPPLIER;

    const result = await getAllDropDownCounterpartiesByCompany(
      Number(currentInvoice.companyId),
      counterpartyType,
      searchParams
    );
    setOptions(result);
    setIsLoading(false);
  }, [
    getAllDropDownCounterpartiesByCompany,
    searchParams,
    watchedIsReimbursement,
    setOptions
  ]);

  const debouncedSearch = useDebounce(getCounterParties, 500);

  useEffect(() => {
    setIsLoading(true);
    debouncedSearch();
  }, [searchParams, debouncedSearch]);

  const handleVerifySection = async () => {
    let counterpartyId =
      selectedCounterPartyId || currentInvoice.counterpartyId;
    const counterpartyName = getValues('counterpartyName');

    if (!counterpartyId && counterpartyName) {
      const createBody: CounterpartyCreateUpdate = {
        name: counterpartyName,
        vatNumber: getValues('counterpartyVatNumber'),
        registrationNumber: getValues('counterpartyRegistrationNumber'),
        ibans: [],
        type: CounterpartyType.SUPPLIER
      };

      const createdCounterparty = await createNewCounterParty(
        Number(currentInvoice.companyId),
        createBody
      );
      counterpartyId = createdCounterparty.id;
    }

    handleVerify(
      {
        counterpartyId,
        counterpartyName,
        counterpartyRegistrationNumber: getValues(
          'counterpartyRegistrationNumber'
        ),
        counterpartyVatNumber: getValues('counterpartyVatNumber'),
        isReimbursement: currentInvoice.isReimbursement,
        ibans:
          watchedIbanList
            ?.map((iban) => iban.iban)
            .filter((iban): iban is string => iban !== undefined) || []
      },
      InvoiceSection.COUNTERPARTY
    );
  };
  // Validation logic for the registration number and vat number
  const isCounterpartyRegistrationNumberValid = useMemo(
    () =>
      validateRegistrationNumber(
        watchedCounterpartyVatNumber,
        watchedCounterpartyRegistrationNumber
      ),
    [watchedCounterpartyRegistrationNumber, watchedCounterpartyVatNumber]
  );

  const isCounterpartyVatValid = useMemo(
    () =>
      validateVatNumber(
        watchedCounterpartyVatNumber,
        watchedCounterpartyRegistrationNumber
      ),
    [
      watchedCounterpartyVatNumber,
      watchedCounterpartyRegistrationNumber,
      isCounterpartyRegistrationNumberValid
    ]
  );

  const handleAutocompleteChange = useCallback(
    (selectedOption?: CounterpartyDropDown) => {
      if (!selectedOption) {
        return;
      }

      setSelectedCounterPartyId(selectedOption.id || null);
      setValue('counterpartyName', selectedOption.name || '');
      setValue(
        'counterpartyRegistrationNumber',
        selectedOption.registrationNumber || ''
      );
      setValue('counterpartyVatNumber', selectedOption.vatNumber || '');
      setValue(
        'ibans',
        selectedOption.ibans?.map((iban) => ({ ...iban, tmpId: uuidv4() })) || [
          {
            ...INITIAL_IBAN_ITEM,
            ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
            tmpId: uuidv4()
          }
        ]
      );
      clearErrors();
    },
    [setSelectedCounterPartyId, setValue, clearErrors]
  );

  useEffect(() => {
    trigger();
  }, [trigger, formState.isValid]);

  useEffect(() => {
    setIsFormValid(formState.isValid);
  }, [formState.isValid]);

  const watchedCounterpartyName = watch('counterpartyName');

  const checkExisting = useCallback(async () => {
    if (!currentInvoice.companyId) {
      return;
    }

    const response = await checkIfCounterpartyExists(
      currentInvoice.companyId,
      getValues('counterpartyName') || '',
      getValues('counterpartyRegistrationNumber') || '',
      getValues('counterpartyVatNumber') || ''
    );

    setIsExisting(response.isCompanyCounterparty);
    setIsFoundInDatabase(response.isFoundInDatabase);
    setCpIsNotFoundInDatabase(!response.isFoundInDatabase);
  }, [
    getValues('counterpartyName'),
    getValues('counterpartyRegistrationNumber'),
    getValues('counterpartyVatNumber'),
    checkIfCounterpartyExists
  ]);

  const debouncedCheckExisting = useDebounce(checkExisting, 200);

  useEffect(() => {
    debouncedCheckExisting();
  }, [
    watchedCounterpartyName,
    getValues('counterpartyName'),
    getValues('counterpartyRegistrationNumber'),
    getValues('counterpartyVatNumber')
  ]);

  const handleNextStep = () => {
    handleGoToMiddleStep();
    saveInvoiceSection(
      getSupplierDataFormValues(
        currentInvoice,
        isExisting,
        selectedCounterPartyId,
        getValues
      ),
      InvoiceSection.COUNTERPARTY
    );
    setCounterpartyFormData({
      counterpartyId: selectedCounterPartyId || currentInvoice.counterpartyId,
      counterpartyName: getValues('counterpartyName'),
      counterpartyRegistrationNumber: getValues(
        'counterpartyRegistrationNumber'
      ),
      counterpartyVatNumber: getValues('counterpartyVatNumber'),
      isReimbursement: getValues('isReimbursement')
    });
  };

  const appendIban = () => {
    append({
      ...INITIAL_IBAN_ITEM,
      ...COUNTERPARTY_IBAN_ADDITIONAL_INITIAL_FIELDS,
      // @ts-ignore tmpId is used for unique identification for each IBAN (even newly created ones)
      tmpId: uuidv4()
    });
  };

  useEffect(() => {
    trigger();
  }, [trigger, currentInvoice]);

  useEffect(() => {
    reset(getSupplierDefaultValues(currentInvoice));
  }, [currentInvoice, reset]);

  return (
    <Box sx={{ pt: 2.5 }}>
      <form
        className="supplier-form"
        onSubmit={handleSubmit(
          isExisting || watchedIsReimbursement
            ? handleVerifySection
            : handleNextStep
        )}
      >
        <ActionButtons
          handleBack={() => handleBack(InvoiceSection.COMPANY)}
          isFormValid={isFormValid}
        />

        <Box sx={verificationFormFields}>
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              height: '55px'
            }}
          >
            <Controller
              name="counterpartyId"
              control={control}
              render={({ field }) => (
                <FormControl sx={verificationFormItem}>
                  <Autocomplete
                    open={
                      open &&
                      !isLoading &&
                      (notEnoughCharactersToSearch || options.length > 0)
                    }
                    disableClearable
                    blurOnSelect
                    clearOnBlur={false}
                    forcePopupIcon={false}
                    onChange={(event, selectedOption) => {
                      handleAutocompleteChange(selectedOption);
                    }}
                    onInputChange={(event, value) => {
                      setSearchParams(value);
                      setIsLoading(true);
                    }}
                    onOpen={() => {
                      setOpen(true);
                    }}
                    onClose={(_event, reason) => {
                      if (reason === 'escape' || reason === 'blur') {
                        setOpen(false);
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) =>
                      getCounterPartySearchOptionLabel(option)
                    }
                    options={options}
                    loading={notEnoughCharactersToSearch ? false : isLoading}
                    noOptionsText={
                      notEnoughCharactersToSearch
                        ? translate('messages.searchRequiredCharacters', {
                            numberOfCharacters: String(
                              COUNTERPARTY_SEARCH_MIN_CHARACTERS
                            )
                          })
                        : translate('labels.noCounterpartiesFound')
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...field}
                        label={translate('labels.counterparty')}
                        onChange={(event) => {
                          setSearchParams(event.target.value);
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment sx={{ ml: 1 }} position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Tooltip
                        key={props.key}
                        title={
                          option.isCompanyCounterparty &&
                          `${translate('messages.counterpartyOf', {
                            companyName: companyName || ''
                          })}`
                        }
                      >
                        <li {...props}>
                          {getCounterPartySearchOptionLabel(option)}
                          {option.isCompanyCounterparty && (
                            <StarRateIcon
                              color="primary"
                              sx={{ ml: 1, mb: 0.5 }}
                            />
                          )}
                        </li>
                      </Tooltip>
                    )}
                  />
                </FormControl>
              )}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              opacity: !isFoundInDatabase && isFormValid ? 1 : 0
            }}
          >
            <WarningIcon
              sx={{
                fontSize: 26,
                color: yellow[900]
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.8rem' },
                textAlign: 'left'
              }}
            >
              {translate('messages.counterpartyNotInDatabase')}
            </Typography>
          </Box>
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              minHeight: '50px'
            }}
          >
            <FormControl sx={verificationFormItem}>
              <Controller
                name="counterpartyName"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={`${
                      watchedIsReimbursement
                        ? translate('labels.reimburseTo')
                        : translate('labels.name')
                    }*`}
                    name="counterpartyName"
                    placeholder={translate('labels.name')}
                    error={!!error}
                    helperText={error && translate('errors.counterpartyName')}
                  />
                )}
              />
            </FormControl>
          </Box>
          {watchedIsReimbursement && (
            <Box
              sx={{
                ...verificationFormFieldsWrapper,
                minHeight: '50px',
                flexDirection: 'column',
                marginTop: 1
              }}
            >
              {watchedIbanList?.map((item, index) => (
                <Box
                  sx={{ ...arrayFieldFormContainer, alignItems: 'center' }}
                  // @ts-ignore tmpId is used for unique identification for each IBAN (even newly created ones)
                  key={item.tmpId}
                >
                  <Box sx={{ ...verificationFormFields, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 70
                      }}
                    >
                      <Controller
                        name={`ibans.${index}.iban`}
                        control={control}
                        rules={{
                          validate: (value) =>
                            value
                              ? validateIban(value) &&
                                uniqueIbanValidation(
                                  translate,
                                  getValues(`ibans.${index}.iban`),
                                  getValues('ibans')
                                )
                              : true
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              trigger();
                            }}
                            label={translate('labels.iban')}
                            error={
                              !!formState.errors.ibans &&
                              !!formState.errors.ibans[index]
                            }
                            placeholder={translate('labels.iban')}
                            sx={smallVerificationFieldWidth}
                          />
                        )}
                      />
                      {formState.errors.ibans &&
                        formState.errors.ibans[index] && (
                          <FormHelperText error>
                            {!validateIban(
                              getValues(`ibans.${index}.iban`) || ''
                            )
                              ? translate('errors.invalidIban')
                              : translate('errors.ibanUnique')}
                          </FormHelperText>
                        )}
                    </Box>
                  </Box>
                </Box>
              ))}
              <Box display="flex" justifyContent="center">
                <Button type="button" onClick={appendIban}>
                  {`+ ${translate('buttons.add')}`}
                </Button>
              </Box>
            </Box>
          )}
          {!watchedIsReimbursement && (
            <Box
              sx={{
                ...verificationFormFieldsWrapper,
                height: 85
              }}
            >
              <Controller
                name="counterpartyRegistrationNumber"
                control={control}
                rules={{
                  validate: () => {
                    if (!watchedCounterpartyVatNumber) {
                      return (
                        isCounterpartyRegistrationNumberValid ||
                        translate('errors.counterpartyRegistrationNumberBG')
                      );
                    }
                    if (watchedCounterpartyVatNumber.startsWith('BG')) {
                      return (
                        isCounterpartyRegistrationNumberValid ||
                        translate('errors.counterpartyRegistrationNumberBG')
                      );
                    }

                    return (
                      isCounterpartyRegistrationNumberValid ||
                      translate('errors.counterpartyRegistrationNumberForeign')
                    );
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!isCounterpartyRegistrationNumberValid}
                    label={translate('labels.companyRegistrationNumber')}
                    name="counterpartyRegistrationNumber"
                    placeholder={translate(
                      'labels.counterpartyRegistrationNumber'
                    )}
                    helperText={
                      !isCounterpartyRegistrationNumberValid &&
                      (watchedCounterpartyVatNumber?.startsWith('BG')
                        ? translate('errors.counterpartyRegistrationNumberBG')
                        : watchedCounterpartyVatNumber === ''
                          ? translate('errors.counterpartyRegistrationNumberBG')
                          : translate(
                              'errors.counterpartyRegistrationNumberForeign'
                            ))
                    }
                    sx={verificationFormItem}
                  />
                )}
              />
              <Controller
                name="counterpartyVatNumber"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value?.startsWith('BG')) {
                      return (
                        isCounterpartyVatValid ||
                        translate('errors.counterpartyVatNumberEquality')
                      );
                    }
                    return true; // No validation error for non-BG VAT numbers
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={translate('labels.vatNumber')}
                    name="counterpartyVatNumber"
                    placeholder={translate('labels.vatNumber')}
                    sx={verificationFormItem}
                    error={!!field.value && !isCounterpartyVatValid}
                    helperText={
                      field.value &&
                      !isCounterpartyVatValid &&
                      translate('errors.counterpartyVatNumberEquality')
                    }
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </form>
    </Box>
  );
};
